'use client';
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { LegalShell, useLang, type Lang } from '@/components/LegalShell';

const C: Record<Lang, { title: string; sub: string; qa: { q: string; a: string }[] }> = {
  uk: {
    title: 'Часті питання', sub: 'Відповіді на найпоширеніші запитання про покупки, доставку й оплату.',
    qa: [
      { q: 'Як я отримаю товар після оплати?', a: 'Цифровий товар (ключ, код або дані доступу) надходить на ваш email і в особистий кабінет автоматично або від менеджера протягом 1–24 годин після підтвердження оплати.' },
      { q: 'Які способи оплати доступні?', a: 'Доступні способи відображаються на сторінці оформлення замовлення. Валюта визначається автоматично за мовою інтерфейсу (₴ / ₽ / $).' },
      { q: 'Чи можна повернути кошти?', a: 'Цифрові товари вважаються використаними після передачі. Повернення можливе, якщо товар не доставлено або не працює з нашої вини — деталі в розділі «Повернення».' },
      { q: 'Товар не активується — що робити?', a: 'Перевірте регіон акаунта й правильність активації. Якщо проблема лишається — зверніться в підтримку з номером замовлення, ми допоможемо або замінимо товар.' },
      { q: 'Чи безпечно купувати?', a: 'Оплата проходить через ліцензованих платіжних операторів, дані карток ми не зберігаємо. Кожне замовлення прив’язане до вашого акаунта.' },
    ],
  },
  ru: {
    title: 'Частые вопросы', sub: 'Ответы на самые распространённые вопросы о покупках, доставке и оплате.',
    qa: [
      { q: 'Как я получу товар после оплаты?', a: 'Цифровой товар (ключ, код или данные доступа) поступает на ваш email и в личный кабинет автоматически или от менеджера в течение 1–24 часов после подтверждения оплаты.' },
      { q: 'Какие способы оплаты доступны?', a: 'Доступные способы отображаются на странице оформления заказа. Валюта определяется автоматически по языку интерфейса (₴ / ₽ / $).' },
      { q: 'Можно ли вернуть средства?', a: 'Цифровые товары считаются использованными после передачи. Возврат возможен, если товар не доставлен или не работает по нашей вине — детали в разделе «Возврат».' },
      { q: 'Товар не активируется — что делать?', a: 'Проверьте регион аккаунта и правильность активации. Если проблема остаётся — обратитесь в поддержку с номером заказа, мы поможем или заменим товар.' },
      { q: 'Безопасно ли покупать?', a: 'Оплата проходит через лицензированных платёжных операторов, данные карт мы не храним. Каждый заказ привязан к вашему аккаунту.' },
    ],
  },
  en: {
    title: 'FAQ', sub: 'Answers to the most common questions about purchases, delivery and payment.',
    qa: [
      { q: 'How do I receive the product after payment?', a: 'The digital product (key, code or access details) is sent to your email and personal account automatically or by a manager within 1–24 hours after payment confirmation.' },
      { q: 'What payment methods are available?', a: 'Available methods are shown at checkout. The currency is set automatically by the interface language (₴ / ₽ / $).' },
      { q: 'Can I get a refund?', a: 'Digital goods are considered used after transfer. A refund is possible if the item was not delivered or does not work through our fault — see the “Refunds” section.' },
      { q: 'The product won’t activate — what should I do?', a: 'Check the account region and activation steps. If the issue persists, contact support with your order number — we will help or replace the item.' },
      { q: 'Is it safe to buy?', a: 'Payments go through licensed payment operators; we do not store card data. Every order is linked to your account.' },
    ],
  },
};

export default function FaqPage() {
  const lang = useLang();
  const c = C[lang];
  const [open, setOpen] = useState<number | null>(0);
  return (
    <LegalShell title={c.title} subtitle={c.sub}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {c.qa.map((item, i) => {
          const on = open === i;
          return (
            <div key={i} style={{ borderRadius: 14, background: 'var(--surface)', border: `1px solid ${on ? 'color-mix(in srgb, var(--gs-green) 35%, transparent)' : 'var(--border)'}`, overflow: 'hidden', transition: 'border-color .15s' }}>
              <button onClick={() => setOpen(on ? null : i)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', padding: '16px 18px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg)' }}>{item.q}</span>
                <ChevronDown size={18} style={{ color: 'var(--fg-3)', flexShrink: 0, transform: on ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
              </button>
              {on && <div style={{ padding: '0 18px 16px', fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.7 }}>{item.a}</div>}
            </div>
          );
        })}
      </div>
    </LegalShell>
  );
}
